import Vue from "vue";
import { ActionTree, MutationTree, GetterTree } from "vuex";

const initialState: DeviceState = {
  information: undefined,
  name: "",
  model: "",
  mdns: undefined,
  ip_settings: undefined,
  mode_button: { pressed: false, reset_allowed: false },
  eInk: { show_ip: false, invert: false, mode: "standard" },
  configMode: "luminex",
};

const getters: GetterTree<DeviceState, RootState> = {
  information(state: DeviceState): DeviceInformation | undefined {
    return state.information;
  },
  name(state: DeviceState): string {
    return state.name;
  },
  model(state: DeviceState): string {
    return state.model;
  },
  modeButton(state: DeviceState): { pressed: boolean; reset_allowed: boolean } {
    return state.mode_button;
  },
  mdns(state: DeviceState): Mdns | undefined {
    return state.mdns;
  },
  ipSettings(state: DeviceState): IpSettings | undefined {
    return state.ip_settings;
  },
  eInk(state: DeviceState): { show_ip: boolean, invert: boolean } {
    return state.eInk;
  },
  configMode(state: DeviceState): string {
    return state.configMode;
  },
  hasEinkDisplay(state: DeviceState): boolean {
    const eink_models = ["GigaCore30i", "GigaCore20t", "GigaCore18t", "GigaCore16t", "GigaCore10t", "GigaCore16tf"];
    return eink_models.includes(state.model);
  },
};

const mutations: MutationTree<DeviceState> = {
  RESET(state: DeviceState) {
    state.information = undefined;
    state.mdns = undefined;
    state.name = "";
    state.model = "";
    state.ip_settings = undefined;
    state.mode_button = { pressed: false, reset_allowed: false };
    state.eInk = { show_ip: false, invert: false, mode: "standard" };
  },

  SET_DEVICE_INFORMATION(state: DeviceState, payload: DeviceInformation) {
    state.information = payload;
  },
  SET_DEVICE_INFORMATION_NAME(state: DeviceState, payload: string) {
    state.name = payload;
  },
  SET_DEVICE_INFORMATION_MODEL(state: DeviceState, payload: string) {
    state.model = payload;
  },
  SET_DEVICE_MDNS(state: DeviceState, payload: Mdns) {
    state.mdns = payload;
  },
  SET_DEVICE_IP_SETTINGS(state: DeviceState, payload: IpSettings) {
    state.ip_settings = payload;
  },
  SET_MODE_BUTTON(state: DeviceState, payload: { pressed: boolean; reset_allowed: boolean }) {
    state.mode_button = payload;
  },
  SET_EINK(state: DeviceState, payload: { show_ip: boolean; invert: boolean; mode: "standard" | "custom" }) {
    state.eInk = payload;
  },
  SET_CONFIG_MODE(state: DeviceState, payload: string) {
    state.configMode = payload;
  },
};

const actions: ActionTree<DeviceState, RootState> = {
  reset({ commit }) {
    commit("RESET");
  },

  read({ commit, rootState, state }) {
    if (state.information) {
      return new Promise((resolve) => resolve(this.getters["device/information"]));
    }

    return rootState.api
      .get(`device`)
      .then((response: { data: DeviceInformation }) => {
        commit("SET_DEVICE_INFORMATION", response.data);
        commit("SET_DEVICE_INFORMATION_NAME", response.data.name);
        commit("SET_DEVICE_INFORMATION_MODEL", response.data.model);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readMdns({ commit, rootState, state }) {
    if (state.mdns) {
      return new Promise((resolve) => resolve(this.getters["device/mdns"]));
    }

    return rootState.api
      .get(`mdns`)
      .then((response: { data: string }) => {
        commit("SET_DEVICE_MDNS", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readName({ commit, rootState, state }, payload?: { skipStore: false; timeout?: number; host?: string }) {
    if (state.information && state.information.name && payload && !payload.skipStore) {
      return new Promise((resolve) => resolve(this.getters["device/name"]));
    }

    let timeout = 60000;
    if (payload && payload.timeout) {
      timeout = payload.timeout;
    }

    return rootState.api
      .get(payload && payload.host ? `${payload.host}/api/device/name` : `device/name`, { timeout })
      .then((response: { data: string }) => {
        commit("SET_DEVICE_INFORMATION_NAME", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readModel({ commit, rootState, state }) {
    if (state.information && state.information.model) {
      return new Promise((resolve) => resolve(this.getters["device/model"]));
    }

    return rootState.api
      .get(`device/model`)
      .then((response: { data: string }) => {
        commit("SET_DEVICE_INFORMATION_MODEL", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readIpSettings({ commit, rootState, state }) {
    if (state.ip_settings) {
      return new Promise((resolve) => resolve(this.getters["device/ip_settings"]));
    }

    return rootState.api
      .get(`ip_settings`)
      .then((response: { data: string }) => {
        commit("SET_DEVICE_IP_SETTINGS", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readEInk({ commit, rootState, state }) {
    return rootState.api
      .get(`eink`)
      .then((response: { data: { show_ip: boolean, invert: boolean } }) => {
        commit("SET_EINK", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  update({ commit, rootState }, payload: DeviceInformation) {
    return rootState.api
      .put(`device`, payload)
      .then((response: { data: DeviceInformation }) => {
        if (response.data && Object.keys(response.data).length > 0) {
          commit("SET_DEVICE_INFORMATION", response.data);
          commit("SET_DEVICE_INFORMATION_NAME", response.data.name);
          commit("SET_DEVICE_INFORMATION_MODEL", response.data.model);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  updateIpSettings({ commit, rootState }, payload: IpSettings) {
    return rootState.api
      .put(`ip_settings`, payload)
      .then((response: { data: IpSettings }) => {
        if (response.data && Object.keys(response.data).length > 0) {
          commit("SET_DEVICE_IP_SETTINGS", response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  updateEInk({ commit, rootState }, payload: { show_ip: boolean, invert: boolean }) {
    return rootState.api
      .put(`eink`, payload)
      .then((response: { data: { show_ip: boolean, invert: boolean } }) => {
        if (response.data && Object.keys(response.data).length > 0) {
          commit("SET_EINK", response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  async uploadCustomEinkImage({ commit, rootState }, payload) {
    const options = { method: "put", body: payload };

    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/eink/custom/upload`, options);

      if (!response.ok) {
        const data = await response.json();

        return Promise.reject(data);
      }

      return Promise.resolve(response.body);
    } catch (e) {
      throw new Error(e);
    }
  },

  async uploadCustomEinkPreviewImage({ commit, rootState }, payload) {
    const options = { method: "put", body: payload };

    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/eink/custom/upload?preview=true`, options);

      if (!response.ok) {
        const data = await response.json();

        return Promise.reject(data);
      }

      return Promise.resolve(response.body);
    } catch (e) {
      throw new Error(e);
    }
  },

  updateMdns({ commit, rootState }, payload: Mdns) {
    return rootState.api
      .put(`mdns`, payload)
      .then((response: { data: Mdns }) => {
        if (response.data && Object.keys(response.data).length > 0) {
          commit("SET_DEVICE_MDNS", response.data);
        }

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  resetDevice({ commit, rootState }, payload: { keep_ip: boolean; keep_profiles: boolean }) {
    return rootState.api
      .put(`reset`, payload)
      .then((response: { data: DeviceInformation }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  rebootDevice({ commit, rootState }, payload: { wait: number }) {
    return rootState.api
      .put(`reboot`, payload)
      .then((response: { data: DeviceInformation }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  activate({ commit, rootState }) {
    return rootState.api
      .put(`device/firmware/activate_alternate`, {})
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readResetAllowed({ commit, rootState }, payload) {
    return rootState.api
      .get(`interface/mode_button`)
      .then((response: { data: {} }) => {
        commit("SET_MODE_BUTTON", response.data);
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  updateResetAllowed({ commit, rootState }, payload) {
    return rootState.api
      .put(`interface/mode_button`, { reset_allowed: payload })
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  identify({ commit, rootState }) {
    return rootState.api
      .put(`identify`, {})
      .then((response: { data: {} }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readConfigMode({ commit, rootState, state }) {
    return rootState.api
      .get(`config/mode`)
      .then((response: { data: string }) => {
        commit("SET_CONFIG_MODE", response.data);

        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  readEula({ commit, rootState, state }) {
    return rootState.api
      .get(`eula`)
      .then((response: { data: string }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },
  readEulaText({ commit, rootState, state }) {
    return rootState.api
      .get(`eula/text`)
      .then((response: { data: string }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  confirmEula({ commit, rootState, state }) {
    return rootState.api
      .put(`eula`, { accepted: true })
      .then((response: { data: string }) => {
        return Promise.resolve(response.data);
      })
      .catch((e: any) => {
        return Promise.reject(e);
      });
  },

  async upgrade({ commit, rootState }, payload) {
    const options = { method: "put", body: payload };
    try {
      const response = await fetch(`${process.env.VUE_APP_API_URL}/firmware`, options);
      if (!response.ok) {
        const data = await response.json();
        return Promise.reject(data);
      }
      return response;
    } catch (e) {
      throw new Error(e);
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
